import React, { useState, useEffect, useCallback } from 'react';
import { http } from '../../../lib/api';
import {
  MessageSquare, WifiOff, RefreshCw, Send, Settings,
  CheckCircle, XCircle, AlertCircle, QrCode, Users, Bell,
  Power, Smartphone, Save, Copy, Link, ChevronDown, Globe,
  ClipboardList, Clock, DollarSign, Wrench, UserPlus, Monitor,
  Calendar, BarChart2, Plus, Trash2
} from 'lucide-react';

const STATUS_INFO = {
  disconnected: { label: 'Desconectado',  color: 'text-gray-500',   bg: 'bg-gray-100',  icon: WifiOff },
  qr_pending:   { label: 'Esperando QR',  color: 'text-yellow-600', bg: 'bg-yellow-50', icon: QrCode },
  connecting:   { label: 'Conectando...', color: 'text-emerald-600',   bg: 'bg-emerald-50',   icon: RefreshCw },
  ready:        { label: 'Conectado',     color: 'text-green-600',  bg: 'bg-green-50',  icon: CheckCircle },
  auth_failure: { label: 'Error de auth', color: 'text-red-600',    bg: 'bg-red-50',    icon: XCircle },
};

const ROUTE_META = {
  nueva_orden:      { label: 'Ticket creado',             icon: ClipboardList, color: 'text-emerald-600',    section: 'tickets' },
  orden_completada: { label: 'Ticket completado',         icon: CheckCircle,   color: 'text-green-600',   section: 'tickets' },
  agendamiento:     { label: 'Nuevo agendamiento',        icon: Clock,         color: 'text-orange-600',  section: 'gestion' },
  pendiente:        { label: 'Nueva tarea pendiente',     icon: Bell,          color: 'text-yellow-600',  section: 'gestion' },
  presupuesto:      { label: 'Nuevo presupuesto',         icon: DollarSign,    color: 'text-emerald-600', section: 'gestion' },
  nuevo_cliente:    { label: 'Nuevo cliente registrado',  icon: UserPlus,      color: 'text-purple-600',  section: 'clientes' },
  nuevo_equipo:     { label: 'Nuevo equipo registrado',   icon: Monitor,       color: 'text-teal-600',  section: 'clientes' },
  webhook:          { label: 'Integraciones externas (Kuma / Apprise)', icon: Globe, color: 'text-gray-600', section: 'ext' },
};

const ROUTE_SECTIONS = [
  { id: 'tickets',  label: '🎫 Tickets de soporte',    desc: 'Notificaciones al crear o completar un ticket de servicio' },
  { id: 'gestion',  label: '📋 Gestión interna',       desc: 'Agendamientos, tareas pendientes y presupuestos' },
  { id: 'clientes', label: '👥 Clientes y equipos',    desc: 'Registros nuevos de clientes o equipos' },
];

// Toggle switch reutilizable
function Toggle({ value, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-1'}`} />
    </button>
  );
}

// Selector de grupo para un tipo
function GroupSelector({ type, groupId, groups, onSelect, onLoadGroups, loading, isReady }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={groupId || ''}
          onChange={(e) => onSelect(type, e.target.value)}
          placeholder="ID del grupo (ej. 120363xxx@g.us) — o vacío para usar el grupo por defecto"
          className="flex-1 min-w-0 text-xs border border-gray-200 rounded-lg px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-green-400 bg-gray-50"
        />
        {isReady && (
          <button
            onClick={async () => { await onLoadGroups(); setOpen(true); }}
            disabled={loading}
            title="Ver grupos disponibles"
            className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs"
          >
            <Users className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && groups.length > 0 && (
        <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden shadow-sm max-h-40 overflow-y-auto">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => { onSelect(type, g.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-green-50 flex justify-between items-center ${groupId === g.id ? 'bg-green-50 font-medium' : ''}`}
            >
              <span className="truncate">{g.name}</span>
              <span className="text-gray-400 ml-2 flex-shrink-0">{g.participants} miembros</span>
            </button>
          ))}
          <button onClick={() => setOpen(false)} className="w-full text-center py-1 text-xs text-gray-400 hover:bg-gray-50 border-t">
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

export default function PanelWhatsApp() {
  const [status, setStatus]   = useState(null);
  const [qrData, setQrData]   = useState(null);
  const [groups, setGroups]   = useState([]);
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [feedback, setFeedback] = useState({ msg: '', type: 'ok' });
  const [activeTab, setActiveTab] = useState('routing');
  const [copied, setCopied]   = useState(false);
  const [scheduler, setScheduler] = useState(null);
  const [savingScheduler, setSavingScheduler] = useState(false);

  const showFeedback = (msg, type = 'ok') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback({ msg: '', type: 'ok' }), 4000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await http.get('/whatsapp/status');
      setStatus(data);
    } catch (e) {
      setStatus({ status: 'disconnected', has_qr: false, last_error: 'Servicio no disponible' });
    }
  }, []);

  const fetchQR = useCallback(async () => {
    try { const { data } = await http.get('/whatsapp/qr'); setQrData(data.qr || null); }
    catch (e) { setQrData(null); }
  }, []);

  const fetchConfig = useCallback(async () => {
    try { const { data } = await http.get('/whatsapp/config'); setConfig(data); }
    catch (e) { setConfig({}); }
  }, []);

  const fetchScheduler = useCallback(async () => {
    try { const { data } = await http.get('/whatsapp/scheduler'); setScheduler(data); }
    catch (e) {}
  }, []);

  useEffect(() => {
    fetchStatus(); fetchConfig(); fetchScheduler();
    const iv = setInterval(() => { fetchStatus(); }, 5000);
    return () => clearInterval(iv);
  }, []);

  // Refrescar el QR cada 8s mientras esté pendiente (WhatsApp rota el QR cada ~20s)
  useEffect(() => {
    if (status?.status === 'qr_pending') {
      fetchQR();
      const qrIv = setInterval(fetchQR, 8000);
      return () => clearInterval(qrIv);
    } else {
      setQrData(null);
    }
  }, [status?.status]);

  const handleConnect = async () => {
    setLoading(true);
    setStatus((s) => ({ ...s, status: 'connecting' }));
    try {
      await http.post('/whatsapp/connect');
      setTimeout(fetchStatus, 2500);
      setTimeout(fetchStatus, 6000);
    } catch (e) { showFeedback(e.response?.data?.detail || e.message, 'error'); }
    setLoading(false);
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try { await http.post('/whatsapp/disconnect'); setTimeout(fetchStatus, 1000); }
    catch (e) { showFeedback(e.message, 'error'); }
    setLoading(false);
  };

  const handleLoadGroups = async () => {
    if (groups.length > 0) return; // ya cargados
    setLoading(true);
    try {
      const { data } = await http.get('/whatsapp/groups', { timeout: 60000 });
      setGroups(data.groups || []);
    } catch (e) { showFeedback('Error al cargar grupos: ' + e.message, 'error'); }
    setLoading(false);
  };

  const handleTest = async () => {
    try {
      const { data: res } = await http.post('/whatsapp/test');
      showFeedback(res.ok ? '✅ Mensaje de prueba enviado al grupo por defecto' : '⚠️ ' + (res.error || res.message), res.ok ? 'ok' : 'error');
    } catch (e) { showFeedback(e.response?.data?.detail || e.message, 'error'); }
  };

  const handleTestType = async (type) => {
    try {
      const { data: res } = await http.post(`/whatsapp/test/${type}`);
      showFeedback(res.ok ? `✅ Prueba enviada para "${ROUTE_META[type]?.label}"` : '⚠️ ' + (res.error || res.message), res.ok ? 'ok' : 'error');
    } catch (e) { showFeedback(e.response?.data?.detail || e.message, 'error'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await http.put('/whatsapp/config', config);
      showFeedback('✅ Configuración guardada');
    } catch (e) { showFeedback(e.message, 'error'); }
    setSaving(false);
  };

  const setRoute = (type, field, value) => {
    setConfig((c) => ({
      ...c,
      routes: { ...c.routes, [type]: { ...c.routes?.[type], [field]: value } }
    }));
  };

  const copyWebhookUrl = () => {
    const url = `${window.location.protocol}//${window.location.host}/api/whatsapp/webhook?token=${config?.webhook_token || ''}`;
    navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveScheduler = async () => {
    setSavingScheduler(true);
    try {
      const { data } = await http.put('/whatsapp/scheduler', scheduler);
      setScheduler(data);
      showFeedback('✅ Horarios de reportes guardados');
    } catch (e) { showFeedback(e.response?.data?.detail || e.message, 'error'); }
    setSavingScheduler(false);
  };

  const handleTestReport = async () => {
    try {
      const { data } = await http.post('/whatsapp/scheduler/test');
      showFeedback(data.ok ? '✅ Reporte enviado al grupo' : '⚠️ ' + (data.error || 'Sin respuesta'), data.ok ? 'ok' : 'error');
    } catch (e) { showFeedback(e.response?.data?.detail || e.message, 'error'); }
  };

  const updateHorario = (i, field, value) =>
    setScheduler(s => ({ ...s, horarios: s.horarios.map((h, idx) => idx === i ? { ...h, [field]: value } : h) }));

  const addHorario = () =>
    setScheduler(s => ({ ...s, horarios: [...(s.horarios || []), { hora: 9, minuto: 0, etiqueta: 'Nuevo horario' }] }));

  const removeHorario = (i) =>
    setScheduler(s => ({ ...s, horarios: s.horarios.filter((_, idx) => idx !== i) }));

  // ─── Render ────────────────────────────────────────────────────────────────
  const st = status?.status || 'disconnected';
  const stInfo = STATUS_INFO[st] || STATUS_INFO.disconnected;
  const StIcon = stInfo.icon;
  const isReady = st === 'ready';

  if (!config) return (
    <div className="flex items-center justify-center p-12 text-gray-400">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Cargando...
    </div>
  );

  const webhookUrl = `${window.location.protocol}//${window.location.host}/api/whatsapp/webhook?token=${config.webhook_token || ''}`;

  return (
    <div className="space-y-5">

      {/* ── Header estado ── */}
      <div className={`p-4 rounded-xl border-2 flex flex-wrap items-center justify-between gap-3 ${stInfo.bg}`} style={{ borderColor: 'currentColor' }}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full bg-white shadow ${stInfo.color}`}>
            <StIcon className={`w-5 h-5 ${st === 'connecting' ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <p className={`font-bold ${stInfo.color}`}>{stInfo.label}</p>
            {status?.last_error && <p className="text-xs text-gray-500">{status.last_error}</p>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isReady ? (
            <button onClick={handleConnect} disabled={loading || st === 'connecting'}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
              <Power className="w-4 h-4" />{st === 'connecting' ? 'Conectando...' : 'Conectar'}
            </button>
          ) : (
            <>
              <button onClick={handleTest}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">
                <Send className="w-3.5 h-3.5" /> Prueba general
              </button>
              <button onClick={handleDisconnect} disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm">
                <WifiOff className="w-3.5 h-3.5" /> Desconectar
              </button>
            </>
          )}
          <button onClick={fetchStatus} className="p-1.5 text-gray-500 hover:bg-white rounded-lg">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── QR ── */}
      {st === 'qr_pending' && (
        <div className="bg-white border-2 border-yellow-300 rounded-xl p-5 text-center">
          <div className="flex items-center gap-2 justify-center mb-3 text-yellow-600">
            <Smartphone className="w-5 h-5" />
            <span className="font-semibold">Escanear con WhatsApp</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
          {qrData
            ? <img src={qrData} alt="QR" className="mx-auto w-52 h-52 rounded-xl shadow-md" />
            : <div className="w-52 h-52 mx-auto flex items-center justify-center bg-gray-100 rounded-xl"><RefreshCw className="w-8 h-8 text-gray-400 animate-spin" /></div>
          }
        </div>
      )}

      {/* ── Feedback ── */}
      {feedback.msg && (
        <div className={`p-3 rounded-lg text-sm ${feedback.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
          {feedback.type === 'error' && <AlertCircle className="inline w-4 h-4 mr-1" />}{feedback.msg}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {[['routing', Bell, 'Enrutamiento'], ['general', Settings, 'General'], ['external', Globe, 'Integraciones externas'], ['reports', Calendar, 'Reportes']].map(([id, Icon, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === id ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* ── Tab: Enrutamiento ── */}
      {activeTab === 'routing' && (
        <div className="space-y-3">
          {/* Grupo por defecto */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="font-medium text-gray-800 text-sm mb-1">Grupo por defecto</p>
            <p className="text-xs text-gray-400 mb-2">Los tipos sin grupo propio enviarán aquí</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={config.default_group || ''}
                onChange={(e) => setConfig((c) => ({ ...c, default_group: e.target.value }))}
                placeholder="120363xxx@g.us"
                className="flex-1 font-mono text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              />
              {isReady && (
                <button onClick={async () => { await handleLoadGroups(); }}
                  disabled={loading}
                  className="flex items-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-sm">
                  <Users className="w-4 h-4" />{loading ? '...' : 'Ver grupos'}
                </button>
              )}
            </div>
            {/* Lista de grupos para el default */}
            {groups.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                {groups.map((g) => (
                  <button key={g.id} onClick={() => setConfig((c) => ({ ...c, default_group: g.id }))}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-green-50 flex justify-between ${config.default_group === g.id ? 'bg-green-50 font-medium' : ''}`}>
                    <span>{g.name}</span><span className="text-gray-400">{g.participants}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rutas por tipo — agrupadas por sección */}
          <div className="space-y-3">
            {ROUTE_SECTIONS.map((section) => {
              const sectionTypes = Object.entries(ROUTE_META).filter(([, m]) => m.section === section.id);
              return (
                <div key={section.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {/* Header de sección */}
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-700">{section.label}</p>
                    <p className="text-xs text-gray-400">{section.desc}</p>
                  </div>
                  {/* Items de la sección */}
                  <div className="divide-y divide-gray-100">
                    {sectionTypes.map(([type, meta]) => {
                      const Icon = meta.icon;
                      const route = config.routes?.[type] || { enabled: false, group_id: '' };
                      return (
                        <div key={type} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${meta.color}`} />
                              <span className="text-sm font-medium text-gray-800">{meta.label}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              {isReady && route.enabled && (
                                <button onClick={() => handleTestType(type)}
                                  title="Enviar prueba"
                                  className="p-1 text-emerald-400 hover:text-emerald-600 rounded">
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <Toggle value={route.enabled} onChange={() => setRoute(type, 'enabled', !route.enabled)} />
                            </div>
                          </div>
                          {route.enabled && (
                            <div>
                              <p className="text-xs text-gray-400 mt-1 mb-1">
                                Grupo destino — vacío = usa el grupo por defecto
                              </p>
                              <GroupSelector
                                type={type}
                                groupId={route.group_id}
                                groups={groups}
                                onSelect={(t, val) => setRoute(t, 'group_id', val)}
                                onLoadGroups={handleLoadGroups}
                                loading={loading}
                                isReady={isReady}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tab: General ── */}
      {activeTab === 'general' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          {/* Habilitar global */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm text-gray-700">Notificaciones WhatsApp globales</p>
              <p className="text-xs text-gray-400">Desactivar apaga todo sin perder la configuración</p>
            </div>
            <Toggle value={config.enabled} onChange={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))} />
          </div>
          {/* Prefijo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prefijo de mensajes</label>
            <input
              type="text"
              value={config.custom_prefix || ''}
              onChange={(e) => setConfig((c) => ({ ...c, custom_prefix: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="🔔 *Elements*"
            />
            <p className="text-xs text-gray-400 mt-1">Se antepone a todos los mensajes</p>
          </div>
          {/* Webhook token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Token del Webhook</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={config.webhook_token || ''}
                onChange={(e) => setConfig((c) => ({ ...c, webhook_token: e.target.value }))}
                className="flex-1 font-mono text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Integraciones externas ── */}
      {activeTab === 'external' && (
        <div className="space-y-4">
          {/* Route webhook */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-500" />
                <p className="font-semibold text-gray-800">Webhook para servicios externos</p>
              </div>
              <Toggle
                value={config.routes?.webhook?.enabled ?? true}
                onChange={() => setRoute('webhook', 'enabled', !(config.routes?.webhook?.enabled ?? true))}
              />
            </div>
            <p className="text-xs text-gray-500">Recibí notificaciones de <strong>Uptime Kuma</strong>, <strong>Apprise</strong> y cualquier servicio que soporte webhooks HTTP</p>

            {/* URL del webhook */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">URL del Webhook (copiá esta URL en tu servicio)</label>
              <div className="flex gap-2">
                <input readOnly value={webhookUrl}
                  className="flex-1 font-mono text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-600" />
                <button onClick={copyWebhookUrl}
                  className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs">
                  {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>

            {/* Grupo destino del webhook */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Grupo destino para alertas externas</label>
              <GroupSelector
                type="webhook"
                groupId={config.routes?.webhook?.group_id || ''}
                groups={groups}
                onSelect={(t, val) => setRoute(t, 'group_id', val)}
                onLoadGroups={handleLoadGroups}
                loading={loading}
                isReady={isReady}
              />
            </div>

            {/* Guías */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-600">Configuración por servicio:</p>

              <div className="p-3 bg-emerald-50 rounded-lg text-xs space-y-1">
                <p className="font-semibold text-emerald-800">🟢 Uptime Kuma</p>
                <p className="text-emerald-700">Settings → Notifications → Webhook</p>
                <p className="text-emerald-700">URL: <code className="bg-white px-1 rounded">{webhookUrl}</code></p>
                <p className="text-emerald-700">Method: <strong>POST</strong> — Content Type: <strong>application/json</strong></p>
              </div>

              <div className="p-3 bg-orange-50 rounded-lg text-xs space-y-1">
                <p className="font-semibold text-orange-800">🔔 Apprise</p>
                <p className="text-orange-700">Agregar canal JSON con la URL del webhook</p>
                <p className="text-orange-700 font-mono text-xs break-all">{webhookUrl}</p>
                <p className="text-orange-600">Body: <code className="bg-white px-1 rounded">{`{"title":"...", "body":"..."}`}</code></p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg text-xs space-y-1">
                <p className="font-semibold text-gray-700">⚙️ Cualquier servicio genérico</p>
                <p className="text-gray-600">POST a la URL con body JSON: <code className="bg-white px-1 rounded">{`{"message": "Texto del alerta"}`}</code></p>
                <p className="text-gray-600">O header: <code className="bg-white px-1 rounded">X-Webhook-Token: {config.webhook_token}</code></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Reportes ── */}
      {activeTab === 'reports' && (
        scheduler ? (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">

              {/* Activar/desactivar */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-gray-800">Reportes diarios automáticos</p>
                  <p className="text-xs text-gray-400">Se envía al grupo seleccionado abajo</p>
                </div>
                <Toggle value={scheduler.enabled} onChange={() => setScheduler(s => ({ ...s, enabled: !s.enabled }))} />
              </div>

              {/* Grupo destino */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grupo destino de los reportes</label>
                <p className="text-xs text-gray-400 mb-2">Si queda vacío, se usará el grupo del tipo "webhook" o el grupo por defecto</p>
                {/* Badge del grupo guardado actualmente */}
                {scheduler.group_id && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Guardado actualmente:</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      ✅ {groups.find(g => g.id === scheduler.group_id)?.name || scheduler.group_id}
                    </span>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={scheduler.group_id || ''}
                    onChange={(e) => setScheduler(s => ({ ...s, group_id: e.target.value }))}
                    placeholder="120363xxx@g.us — o vacío para usar el grupo por defecto"
                    className="flex-1 font-mono text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 bg-gray-50"
                  />
                  {isReady && (
                    <button onClick={async () => { await handleLoadGroups(); }}
                      disabled={loading}
                      className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-sm">
                      <Users className="w-4 h-4" />{loading ? '...' : 'Ver grupos'}
                    </button>
                  )}
                </div>
                {groups.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                    {groups.map((g) => (
                      <button key={g.id} onClick={() => setScheduler(s => ({ ...s, group_id: g.id }))}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-green-50 flex justify-between ${scheduler.group_id === g.id ? 'bg-green-50 font-medium' : ''}`}>
                        <span>{g.name}</span>
                        <span className="text-gray-400">{g.participants} miembros</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Secciones */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Secciones incluidas en el reporte</p>
                <div className="space-y-2">
                  {[
                    ['incluir_pendientes',    '📌 Pendientes sin completar'],
                    ['incluir_agendamientos', '🗓️ Agendamientos del día sin completar'],
                    ['incluir_visitas',       '🏢 Visitas del calendario para hoy'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-sm text-gray-700">{label}</span>
                      <Toggle value={scheduler[key] ?? true} onChange={() => setScheduler(s => ({ ...s, [key]: !(s[key] ?? true) }))} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Horarios */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">Horarios de envío</p>
                  <button onClick={addHorario}
                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium">
                    <Plus className="w-3.5 h-3.5" /> Agregar horario
                  </button>
                </div>
                <div className="space-y-2">
                  {(scheduler.horarios || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" max="23" value={h.hora}
                          onChange={(e) => updateHorario(i, 'hora', Math.min(23, Math.max(0, +e.target.value)))}
                          className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center font-mono focus:ring-2 focus:ring-green-400 focus:outline-none" />
                        <span className="text-gray-500 font-bold">:</span>
                        <input type="number" min="0" max="59" value={h.minuto}
                          onChange={(e) => updateHorario(i, 'minuto', Math.min(59, Math.max(0, +e.target.value)))}
                          className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center font-mono focus:ring-2 focus:ring-green-400 focus:outline-none" />
                      </div>
                      <input type="text" value={h.etiqueta || ''}
                        onChange={(e) => updateHorario(i, 'etiqueta', e.target.value)}
                        placeholder="Etiqueta (ej. Inicio del día)"
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-green-400 focus:outline-none" />
                      <button onClick={() => removeHorario(i)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {(scheduler.horarios || []).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-3">Sin horarios. Agregá al menos uno.</p>
                  )}
                </div>
              </div>

              {/* Próximas ejecuciones */}
              {scheduler.jobs?.length > 0 && (
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs font-medium text-emerald-700 mb-1">Próximos envíos programados</p>
                  {scheduler.jobs.map(j => (
                    <p key={j.id} className="text-xs text-emerald-600">🕐 {j.next_run}</p>
                  ))}
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button onClick={handleSaveScheduler} disabled={savingScheduler} title="Guardar toda la configuración de reportes"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
                  {savingScheduler ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingScheduler ? 'Guardando...' : 'Guardar configuración'}
                </button>
                <button onClick={handleTestReport}
                  title="Enviar el reporte ahora mismo (para probar)"
                  className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium">
                  <Send className="w-4 h-4" /> Enviar ahora
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center p-8 text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Cargando...
          </div>
        )
      )}

      {/* ── Guardar config WA (solo visible en tabs que no sean reports) ── */}
      {activeTab !== 'reports' && (
        <div className="flex justify-end pb-2">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </div>
      )}
    </div>
  );
}
